/**
 * /bug-report-template — SEO landing page targeting the "bug report
 * template" / "bug report form template" / "software bug report template"
 * cluster.
 *
 * Differentiation angle: a bug report in Jira, Linear, or a Google Form is
 * a self-contained ticket — it describes what broke, but it's severed from
 * the spec or decision that defined what "correct" was supposed to be. This
 * page hands the reader the actual template (the eight fields, spelled out)
 * so a searcher gets something usable, then shows that Draftlize lets a bug
 * card link to the spec/decision it violates: the agent fixing it reads the
 * original intent, and when the spec changes, related bugs get tracked.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and the
 * comparison pages. Chinese variant lives in BugReportTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function BugReportTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/bug-report-template"
      documentTitle="Bug Report Template (Linked to the Spec It Breaks) · Draftlize"
      documentDescription="A bug report template you can copy — Title, Environment, Steps to reproduce, Expected, Actual, Severity, Attachments, Related spec or decision. The catch: a software bug report template in Jira or a form is an island, cut off from the spec that defined correct. In Draftlize a bug card links to the spec or decision it violates, so the agent fixing it reads the original intent."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Bug Reports</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A bug report template that links the bug<br/>
            <em>to the decision it violates.</em>
          </h1>
          <p className="lede">
            You came for a <em>bug report template</em> — the fields, the headings, something to paste into Jira or a form. It's below; take it. But a good <em>software bug report template</em> answers one question the standard fields never do: <em>what was this supposed to do in the first place?</em> A ticket in Jira or a Google Form is an island — it describes what broke, severed from the spec or decision that defined "correct." Draftlize gives you the same eight fields — and makes the last one a real link, so a bug card points at the spec it violates, and the agent fixing it reads the original intent before it writes a line.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight fields. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole bug report template. It works in a Jira ticket, a Linear issue, or a bug report form — paste it as-is. The next section is about what changes when the last field stops being a free-text note and becomes a link to the spec the bug broke.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Title',
              hint: 'Required',
              body: 'One line: component, the broken behavior, the trigger. "Checkout — total ignores the per-use discount when a coupon is applied." A title someone can triage without opening the ticket.',
            },
            {
              k: 'Environment',
              hint: 'Where',
              body: 'Build / version, OS, browser, device, and the account or data state. Half of "cannot reproduce" lives here — the bug is real, it just needed the environment you forgot to write down.',
            },
            {
              k: 'Steps to reproduce',
              hint: 'How',
              body: 'Numbered, from a known starting state, with the exact inputs. If a stranger can\'t follow them to the failure, the report isn\'t done. This is the field that decides whether the bug gets fixed or bounced back.',
            },
            {
              k: 'Expected behavior',
              hint: 'The intent',
              body: 'What should have happened — and, ideally, the spec or decision that says so. This is where most templates wave their hands. "Correct" isn\'t an opinion; it was defined somewhere. Point at it.',
            },
            {
              k: 'Actual behavior',
              hint: 'The break',
              body: 'What actually happened: the wrong output, the error, the stack trace, the screenshot. Concrete and observable, not "it\'s broken." The gap between this and Expected is the bug.',
            },
            {
              k: 'Severity',
              hint: 'How bad',
              body: 'Blocker, major, minor, trivial — plus impact: who hits it, how often, is there a workaround. Severity is what an agent or a human sorts the queue by; guess it honestly.',
            },
            {
              k: 'Attachments',
              hint: 'Evidence',
              body: 'Screenshots, a screen recording, logs, a HAR file, the failing payload. The proof that turns "I think it\'s broken" into a reproducible fact someone can act on.',
            },
            {
              k: 'Related spec or decision',
              hint: 'What it violates',
              body: 'The spec, requirement, or product decision this bug contradicts. In a form this is a dead URL you\'ll paste and forget. It\'s the field that says what "fixed" even means — and the one this whole page is about.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why a ticket cut off from its spec is half a bug report — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a bug report <em>isn't enough.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A ticket describes the break, not the intent</h3>
            <p>The fields are good — that's why every bug report form has roughly this shape. But they capture what went wrong, never what right was. "Expected behavior" is whatever the reporter remembers the rule to be, which is often not the rule. The spec that actually defined correct lives in another tool, if it lives at all.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The fix loses the original reasoning</h3>
            <p>Whoever picks up the bug — a developer or an AI agent — patches the symptom in the report. They never see the decision that set the behavior, so they "fix" it back to something that violates a different rule, and you ship a second bug to undo the first.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">When the spec changes, nobody re-checks the bugs</h3>
            <p>You change a pricing or flow decision. Somewhere there are open bugs filed against the old behavior — now obsolete, or now pointing at the wrong "expected." Nothing connects the spec to those tickets, so they rot in the backlog until someone wastes an afternoon on a bug that no longer exists.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>wired to the spec.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A bug is a card, linked to what it violates</h3>
            <p>In Draftlize the bug report isn't a stranded ticket — it's a typed card with those exact fields, and "Related spec or decision" is a real link to the spec or decision card it breaks. The bug and the intent it contradicts sit one click apart, in the same structured graph.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The agent reads the original intent before it fixes</h3>
            <p>Claude Code or Cursor, over MCP, opens the linked spec and decision before it touches the code. It fixes toward the behavior that was actually specified — not the reporter's half-memory of it — so the patch restores intent instead of guessing at it.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Change the spec, related bugs get tracked</h3>
            <p>Because the link is real and not a pasted URL, changing a spec flags every bug filed against it — the way a build system invalidates everything downstream of a changed file. A bug whose "expected" just moved gets surfaced, not silently stranded in the backlog.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A bug is the distance between what happened and what was supposed to happen. A report that omits the second half is only half a bug.
        </blockquote>
        <cite>Keep the eight fields. Link the last one to the spec it broke.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What makes a good bug report?', a: 'A good bug report states the distance between what happened and what was supposed to happen: clear steps to reproduce, the actual result, the expected result, environment, severity, and a link to the spec or decision the behavior violates. The expected-result field is the half most reports omit — and without it a bug is only half described.' },
            { q: 'What should a bug report template include?', a: 'Title, steps to reproduce, actual result, expected result, environment or version, severity or priority, screenshots or logs, and the related spec or decision. Steps to reproduce and expected result do the heavy lifting: they turn "it is broken" into something an engineer, or an agent, can act on without a back-and-forth.' },
            { q: 'What is the difference between a bug and a feature request?', a: 'A bug is a gap between the product and its own spec — it is not doing what it was defined to do. A feature request asks the product to do something new it was never specified to do. One restores intended behavior; the other adds intent. Filing them the same way blurs a line worth keeping sharp.' },
            { q: 'How does Draftlize keep bugs tied to intent?', a: 'The related-spec field is a real link, so a bug sits one click from the behavior it violates, and a coding agent reads that intent before it patches. Change the spec and every bug filed against the old behavior flags stale — an obsolete ticket gets surfaced instead of rotting in the backlog.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start filing linked bugs <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project where every bug links to the spec it violates — so the agent fixing it reads the original intent, and a changed spec never leaves a stale bug behind.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
